/**
 * TakeHistoryModal — iter-246
 *
 * Floating panel that shows the saved takes for ONE channel (the one
 * whose History badge the user clicked). Each row:
 *   • Take label (defaults to "Take N")
 *   • Duration in seconds, two decimals
 *   • Captured X min/sec ago (live-updates while the modal is open)
 *   • REVERT button that swaps the channel buffer + closes the modal
 *   • Small DELETE button to drop that single take from history
 *
 * The history is in-memory only — closing the studio loses it. That
 * caveat is surfaced in the footer so users know not to rely on it
 * across reloads.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { History, RotateCcw, Trash2, X, Disc3, Clock, Play, Square, ArrowLeftRight, Headphones } from "lucide-react";
import { padCh } from "@/components/daw/dawHelpers";
import useABCompare from "@/lib/useABCompare";
import MicroWaveform, { computePeaks } from "@/components/daw/MicroWaveform";

function ago(ms) {
  const sec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}

export default function TakeHistoryModal({
  open,
  onClose,
  channelIdx,
  channelName,
  takes,           // [{ id, durationSec, capturedAt, label }] — newest first
  onRevert,        // (takeId) => void
  onDeleteTake,    // (takeId) => void
  onClearChannel,  // () => void
}) {
  const panelRef = useRef(null);
  // tick state to refresh the "X min ago" labels every 10 s.
  const [tick, setTick] = useState(0);
  // iter-247 — A/B compare slot state. Stores take IDs so the slots
  // survive UI re-renders (the actual buffer is looked up from `takes`).
  const [slotA, setSlotA] = useState(null);
  const [slotB, setSlotB] = useState(null);
  const ab = useABCompare();
  // iter-249 — Solo This Take preview. Plays a take in isolation without
  // assigning it to a slot. We piggyback on the SAME `useABCompare` hook
  // (so a Solo preview cancels any active A/B compare playback) and
  // tag the active slot with the take's id when soloing.
  const [soloId, setSoloId] = useState(null);

  // iter-248 — compute a 24-bar peak preview per take ONCE per list
  // change. Keyed on the joined take-id list so adding a new take
  // doesn't force every existing take to recompute the heavy
  // getChannelData → bucket-peak loop.
  // Must run unconditionally (before any early return) to satisfy the
  // rules of hooks.
  const safeTakesForMemo = Array.isArray(takes) ? takes : [];
  const peaksKey = safeTakesForMemo.map((t) => t.id).join("|");
  const peaksById = useMemo(() => {
    const out = {};
    for (const t of safeTakesForMemo) {
      out[t.id] = computePeaks(t.buffer, 24);
    }
    return out;
    // peaksKey is the deliberate cache key (joined take ids) so unchanged
    // takes never re-run the heavy getChannelData → bucket-peak loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peaksKey, safeTakesForMemo]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); }
    };
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("pointerdown", onClick, true);
    const tickT = setInterval(() => setTick((t) => t + 1), 10000);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("pointerdown", onClick, true);
      clearInterval(tickT);
    };
  }, [open, onClose]);

  // iter-247 — Reset slots + stop playback every time the modal closes
  // or switches to a different channel. Stale slot IDs from a previous
  // channel would otherwise resolve to undefined takes.
  useEffect(() => {
    if (!open) {
      setSlotA(null);
      setSlotB(null);
      setSoloId(null);
      ab.stop();
    }
  }, [open, channelIdx, ab]);

  // iter-249 — Auto-clear `soloId` when the A/B player stops naturally
  // (buffer reached its end). Without this the Solo button would still
  // appear "playing" forever.
  useEffect(() => {
    if (ab.activeSlot === null && soloId !== null) setSoloId(null);
  }, [ab.activeSlot, soloId]);

  if (!open || channelIdx === null || channelIdx === undefined) return null;
  const safeTakes = Array.isArray(takes) ? takes : [];
  void tick; // referenced so eslint doesn't flag it as unused

  // iter-247 — resolve slot IDs to actual take objects (newest list ref).
  const takeA = safeTakes.find((t) => t.id === slotA) || null;
  const takeB = safeTakes.find((t) => t.id === slotB) || null;
  const slotOf = (id) => (id === slotA ? "A" : id === slotB ? "B" : null);
  const assignSlot = (slot, id) => {
    if (slot === "A") {
      setSlotA(id === slotA ? null : id);
      if (id === slotB) setSlotB(null);
    } else {
      setSlotB(id === slotB ? null : id);
      if (id === slotA) setSlotA(null);
    }
    ab.stop();
  };

  return (
    <AnimatePresence>
      <motion.div
        key="take-history-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-md p-4"
        data-testid="take-history-overlay"
      >
        <motion.div
          ref={panelRef}
          initial={{ opacity: 0, scale: 0.96, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 10 }}
          transition={{ duration: 0.18 }}
          className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950/95 shadow-2xl"
          data-testid="take-history-modal"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3 border-b border-white/[0.07] px-5 pt-4 pb-3">
            <div className="flex items-center gap-2.5">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-emerald-500/15 ring-1 ring-emerald-400/30 text-emerald-200">
                <History className="h-4 w-4" />
              </span>
              <div>
                <h2 className="font-mono text-[10px] uppercase tracking-wider text-emerald-200">Take History</h2>
                <p className="mt-0.5 text-sm text-white/85">
                  CH {padCh(channelIdx)} · <span className="text-white/55">{channelName || "—"}</span>
                </p>
              </div>
            </div>
            <button
              data-testid="take-history-close"
              onClick={onClose}
              className="rounded-md p-1 text-white/50 transition hover:bg-white/10 hover:text-white"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="max-h-[55vh] overflow-y-auto px-5 py-3">
            {safeTakes.length === 0 ? (
              <div className="grid place-items-center py-10 text-center">
                <Disc3 className="mb-2 h-8 w-8 text-white/15" />
                <p className="text-sm text-white/55">No saved takes yet</p>
                <p className="mt-1 text-[11px] text-white/35">
                  Recording onto this channel will push the previous take into history.
                </p>
              </div>
            ) : (
              <ul className="space-y-2">
                {safeTakes.map((t, i) => {
                  const inSlot = slotOf(t.id); // 'A' | 'B' | null
                  return (
                  <li
                    key={t.id}
                    data-testid={`take-history-item-${t.id}`}
                    className={`group flex items-start gap-3 rounded-lg border px-3 py-2 transition ${
                      inSlot === "A"
                        ? "border-violet-400/45 bg-violet-500/[0.07]"
                        : inSlot === "B"
                        ? "border-amber-400/45 bg-amber-500/[0.07]"
                        : "border-white/[0.06] bg-white/[0.03] hover:border-emerald-400/30 hover:bg-emerald-500/[0.04]"
                    }`}
                  >
                    <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-white/[0.05] font-mono text-[10px] text-white/60">
                      {i + 1}
                    </span>
                    {/* iter-248 — micro-waveform thumbnail so visually
                        identical "Take N" labels can still be told apart
                        at a glance. */}
                    <MicroWaveform
                      peaks={peaksById[t.id]}
                      width={64}
                      height={20}
                      className={`mt-0.5 flex-shrink-0 ${
                        inSlot === "A" ? "text-violet-200"
                        : inSlot === "B" ? "text-amber-200"
                        : "text-white/40"
                      }`}
                      title={`${t.label} waveform`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm text-white/90">{t.label}</p>
                      <div className="mt-0.5 flex items-center gap-2 font-mono text-[10px] text-white/45">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-2.5 w-2.5" />
                          {t.durationSec.toFixed(2)}s
                        </span>
                        <span>·</span>
                        <span>{ago(t.capturedAt)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {/* iter-249 — Solo this take. Plays the buffer in
                          isolation without committing to an A/B slot. Uses
                          the same `useABCompare` hook so it gracefully
                          interrupts any in-progress comparison. */}
                      <button
                        type="button"
                        data-testid={`take-history-solo-${t.id}`}
                        onClick={() => {
                          if (soloId === t.id) {
                            ab.stop();
                            setSoloId(null);
                          } else {
                            // Soloing also clears the previous A/B
                            // active state — `play` does that internally.
                            setSoloId(t.id);
                            ab.play("solo", t.buffer);
                          }
                        }}
                        title={soloId === t.id ? "Stop solo preview" : "Solo this take (preview only)"}
                        className={`grid h-6 w-6 place-items-center rounded-md font-mono text-[10px] font-semibold transition ${
                          soloId === t.id
                            ? "bg-cyan-400/40 text-cyan-50 ring-1 ring-cyan-300/70"
                            : "bg-white/[0.04] text-white/45 ring-1 ring-white/10 hover:bg-cyan-500/20 hover:text-cyan-100"
                        }`}
                      >
                        {soloId === t.id ? <Square className="h-3 w-3" /> : <Headphones className="h-3 w-3" />}
                      </button>
                      {/* iter-247 — A/B slot pills. Click to assign to slot A or B
                          (toggle off if already in that slot). Selecting in one
                          slot clears the other if the same take is already there. */}
                      <button
                        type="button"
                        data-testid={`take-history-slot-a-${t.id}`}
                        onClick={() => assignSlot("A", t.id)}
                        title="Use in A slot"
                        className={`grid h-6 w-6 place-items-center rounded-md font-mono text-[10px] font-semibold transition ${
                          inSlot === "A"
                            ? "bg-violet-400/40 text-violet-50 ring-1 ring-violet-300/70"
                            : "bg-white/[0.04] text-white/45 ring-1 ring-white/10 hover:bg-violet-500/20 hover:text-violet-100"
                        }`}
                      >A</button>
                      <button
                        type="button"
                        data-testid={`take-history-slot-b-${t.id}`}
                        onClick={() => assignSlot("B", t.id)}
                        title="Use in B slot"
                        className={`grid h-6 w-6 place-items-center rounded-md font-mono text-[10px] font-semibold transition ${
                          inSlot === "B"
                            ? "bg-amber-400/40 text-amber-50 ring-1 ring-amber-300/70"
                            : "bg-white/[0.04] text-white/45 ring-1 ring-white/10 hover:bg-amber-500/20 hover:text-amber-100"
                        }`}
                      >B</button>
                      <button
                        type="button"
                        data-testid={`take-history-revert-${t.id}`}
                        onClick={() => onRevert?.(t.id)}
                        className="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-emerald-200 transition hover:bg-emerald-500/30 hover:text-emerald-100"
                      >
                        <RotateCcw className="h-3 w-3" />
                        Revert
                      </button>
                      <button
                        type="button"
                        data-testid={`take-history-delete-${t.id}`}
                        onClick={() => onDeleteTake?.(t.id)}
                        className="rounded-md p-1 text-white/30 transition hover:bg-rose-500/20 hover:text-rose-300"
                        title="Delete this take"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* iter-247 — A/B Compare panel. Visible when at least ONE slot
              is filled; encourages the user to pick a second take by
              greying out the disabled controls. */}
          {(takeA || takeB) && (
            <div
              data-testid="take-history-ab-panel"
              className="border-t border-white/[0.07] bg-white/[0.015] px-5 py-3"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="font-mono text-[9.5px] uppercase tracking-wider text-cyan-200">
                  Compare A/B
                </div>
                {ab.activeSlot && (
                  <div className="font-mono text-[10px] text-white/55">
                    {ab.time.toFixed(2)}s / {ab.duration.toFixed(2)}s
                  </div>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2">
                {/* A slot */}
                <button
                  type="button"
                  data-testid="take-history-play-a"
                  disabled={!takeA}
                  onClick={() => takeA && ab.toggle("A", takeA.buffer)}
                  className={`flex-1 inline-flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left transition ${
                    !takeA
                      ? "border-white/[0.06] bg-white/[0.02] text-white/25 cursor-not-allowed"
                      : ab.activeSlot === "A"
                      ? "border-violet-300 bg-violet-500/30 text-violet-50 shadow-[0_0_18px_rgba(167,139,250,0.35)]"
                      : "border-violet-400/30 bg-violet-500/[0.08] text-violet-100 hover:bg-violet-500/15"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="grid h-5 w-5 place-items-center rounded font-mono text-[10px] font-bold">A</span>
                    {ab.activeSlot === "A" ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                  </span>
                  {/* iter-248 — preview waveform inside the A pill. */}
                  {takeA && (
                    <MicroWaveform
                      peaks={peaksById[takeA.id]}
                      width={70}
                      height={22}
                      className={ab.activeSlot === "A" ? "text-violet-50" : "text-violet-300/80"}
                    />
                  )}
                  <span className="flex-1 truncate text-right font-mono text-[10px] text-white/65">
                    {takeA ? takeA.label : "Pick a take"}
                  </span>
                </button>
                {/* Swap button — flips active slot mid-compare. */}
                <button
                  type="button"
                  data-testid="take-history-swap"
                  disabled={!takeA || !takeB}
                  onClick={() => ab.swap(takeA?.buffer, takeB?.buffer)}
                  title="Swap A ↔ B"
                  className={`grid h-9 w-9 place-items-center rounded-lg border transition ${
                    !takeA || !takeB
                      ? "border-white/[0.06] bg-white/[0.02] text-white/25 cursor-not-allowed"
                      : "border-cyan-400/30 bg-cyan-500/[0.08] text-cyan-200 hover:bg-cyan-500/20"
                  }`}
                >
                  <ArrowLeftRight className="h-3.5 w-3.5" />
                </button>
                {/* B slot */}
                <button
                  type="button"
                  data-testid="take-history-play-b"
                  disabled={!takeB}
                  onClick={() => takeB && ab.toggle("B", takeB.buffer)}
                  className={`flex-1 inline-flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left transition ${
                    !takeB
                      ? "border-white/[0.06] bg-white/[0.02] text-white/25 cursor-not-allowed"
                      : ab.activeSlot === "B"
                      ? "border-amber-300 bg-amber-500/30 text-amber-50 shadow-[0_0_18px_rgba(251,191,36,0.35)]"
                      : "border-amber-400/30 bg-amber-500/[0.08] text-amber-100 hover:bg-amber-500/15"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="grid h-5 w-5 place-items-center rounded font-mono text-[10px] font-bold">B</span>
                    {ab.activeSlot === "B" ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                  </span>
                  {/* iter-248 — preview waveform inside the B pill. */}
                  {takeB && (
                    <MicroWaveform
                      peaks={peaksById[takeB.id]}
                      width={70}
                      height={22}
                      className={ab.activeSlot === "B" ? "text-amber-50" : "text-amber-300/80"}
                    />
                  )}
                  <span className="flex-1 truncate text-right font-mono text-[10px] text-white/65">
                    {takeB ? takeB.label : "Pick a take"}
                  </span>
                </button>
              </div>
              {ab.activeSlot && (
                /* Progress bar — purely visual indicator of the active take's playhead. */
                <div className="mt-2 h-1 overflow-hidden rounded-full bg-white/[0.06]">
                  <div
                    className={`h-full ${ab.activeSlot === "A" ? "bg-violet-300" : "bg-amber-300"}`}
                    style={{ width: `${Math.min(100, (ab.time / Math.max(ab.duration, 0.01)) * 100)}%` }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 border-t border-white/[0.07] px-5 py-3">
            <p className="font-mono text-[9.5px] uppercase tracking-wider text-white/35">
              In-memory · {safeTakes.length} take{safeTakes.length === 1 ? "" : "s"} · lost on refresh
            </p>
            {safeTakes.length > 0 && (
              <button
                type="button"
                data-testid="take-history-clear-all"
                onClick={onClearChannel}
                className="font-mono text-[10px] uppercase tracking-wider text-white/45 transition hover:text-rose-300"
              >
                Clear all
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
